import { AddRounded, ArrowBack, DeleteOutline } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import AmountField from "@/components/AmountField";
import SourcePicker, { type SourceOption } from "@/components/accounting/SourcePicker";
import { DATE_INPUT_LANG } from "@/constants/dates";
import { useCreateJournalEntry, usePostableAccounts } from "@/hooks/useAccounting";
import type { DraftJournalLine, PostableAccount } from "@/interfaces/accounting";
import { formatAmount as money } from "@/utils/number-format";

const today = () => new Date().toISOString().slice(0, 10);
const newLine = (): DraftJournalLine => ({
  key: Math.random().toString(36).slice(2),
  accountKey: "",
  drCr: "debit",
  amount: "",
});

const accountLabel = (a: PostableAccount) =>
  `${a.qbAccountNumber ?? "—"} · ${a.qbAccountName ?? a.accountKey}`;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        letterSpacing: "0.6px",
        textTransform: "uppercase",
        color: "var(--color-graphite)",
        fontWeight: 600,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * New Journal Entry — a hand-keyed entry, for corrections and adjustments that no event produced.
 *
 * Organisation and group are read-only and derived from the source, never typed: an entry tagged to
 * the wrong client still balances and still exports, so the mistake would only surface in a
 * client-level reconciliation months later.
 *
 * Post is disabled until debits equal credits. The server re-checks — this is a courtesy so the
 * imbalance is visible while typing rather than on submit.
 */
export default function NewJournalEntry({ onDone }: { onDone: () => void }) {
  const [accountingDate, setAccountingDate] = useState(today());
  const [postingDate, setPostingDate] = useState(today());
  const [sourceType, setSourceType] = useState<number | "">("");
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [selectedSource, setSelectedSource] = useState<SourceOption | null>(null);
  const [memo, setMemo] = useState("");
  const [lines, setLines] = useState<DraftJournalLine[]>([newLine(), newLine()]);
  const [error, setError] = useState<string | null>(null);

  const { data: accounts = [], isLoading: accountsLoading } = usePostableAccounts();
  const create = useCreateJournalEntry();

  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const l of lines) {
      const v = Number(l.amount);
      if (!Number.isFinite(v) || v <= 0) continue;
      if (l.drCr === "debit") debit += v;
      else credit += v;
    }
    return { debit, credit, balanced: Math.abs(debit - credit) < 0.005 && debit > 0 };
  }, [lines]);

  const complete = lines.filter((l) => l.accountKey && Number(l.amount) > 0);
  const canPost = totals.balanced && complete.length >= 2 && accountingDate !== "";

  const setLine = (key: string, patch: Partial<DraftJournalLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const submit = async () => {
    setError(null);
    try {
      await create.mutateAsync({
        accountingDate,
        postingDate: postingDate || undefined,
        ...(sourceType !== "" && sourceId != null
          ? { sourceType: sourceType as number, sourceId }
          : {}),
        memo: memo.trim() || undefined,
        lines: complete.map((l) => ({
          accountKey: l.accountKey,
          drCr: l.drCr,
          amount: Number(l.amount),
        })),
      });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't post the journal entry.");
    }
  };

  const card = {
    border: "1px solid var(--color-card-border)",
    borderRadius: "var(--radius-card)",
    p: { xs: 2, md: 3 },
    mb: 3,
  } as const;

  return (
    <Box sx={{ fontFamily: "var(--font-lato)" }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={onDone}
        sx={{ textTransform: "none", color: "var(--color-graphite)", mb: 2 }}
      >
        Journal Entries
      </Button>
      <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 3 }}>New journal entry</Typography>

      {/* Entry-level detail. Source is per ENTRY, not per line — journal_entries carries one
          source_type / source_id, and org + group hang off it. */}
      <Box sx={card}>
        <SectionTitle>Entry</SectionTitle>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 2,
          }}
        >
          <TextField
            label="Accounting date"
            type="date"
            lang={DATE_INPUT_LANG}
            size="small"
            value={accountingDate}
            onChange={(e) => setAccountingDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="The day this belongs to"
          />
          <TextField
            label="Posting date"
            type="date"
            lang={DATE_INPUT_LANG}
            size="small"
            value={postingDate}
            onChange={(e) => setPostingDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="The day it's recorded"
          />
          <SourcePicker
            sourceType={sourceType}
            sourceId={sourceId}
            onChange={(t, id, sel) => {
              setSourceType(t);
              setSourceId(id);
              setSelectedSource(sel);
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 2fr" },
            gap: 2,
            mt: 2,
          }}
        >
          {/* Read-only: derived from the source on the server, shown here so it's visible before posting. */}
          <TextField
            label="Organization"
            size="small"
            value={selectedSource?.organizationName ?? "—"}
            slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
          />
          <TextField
            label="Group"
            size="small"
            value={selectedSource?.groupName ?? "—"}
            slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
          />
          <TextField
            label="Memo"
            size="small"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="What this entry is for"
          />
        </Box>
      </Box>

      {/* Lines */}
      <Box sx={card}>
        <SectionTitle>Lines</SectionTitle>
        {accountsLoading ? (
          <CircularProgress aria-label="Loading" size={22} />
        ) : (
          <>
            {lines.map((l) => (
              <Box
                key={l.key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 140px 160px 44px" },
                  gap: 1.5,
                  mb: 1.5,
                  alignItems: "start",
                }}
              >
                <Autocomplete
                  size="small"
                  options={accounts}
                  value={accounts.find((a) => a.accountKey === l.accountKey) ?? null}
                  onChange={(_, v) => setLine(l.key, { accountKey: v?.accountKey ?? "" })}
                  getOptionLabel={accountLabel}
                  isOptionEqualToValue={(a, b) => a.accountKey === b.accountKey}
                  renderInput={(params) => <TextField {...params} label="Account" />}
                />
                <TextField
                  select
                  size="small"
                  label="Dr / Cr"
                  value={l.drCr}
                  onChange={(e) => setLine(l.key, { drCr: e.target.value as "debit" | "credit" })}
                >
                  <MenuItem value="debit">Debit</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                </TextField>
                <AmountField value={l.amount} onChange={(amount) => setLine(l.key, { amount })} />
                <IconButton
                  aria-label="Remove line"
                  disabled={lines.length <= 2}
                  onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddRounded />}
              onClick={() => setLines((ls) => [...ls, newLine()])}
              sx={{ textTransform: "none", mt: 0.5 }}
            >
              Add line
            </Button>
          </>
        )}

        {/* Running totals — an entry that doesn't balance can't be posted, so show it while typing. */}
        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: "1px solid var(--color-card-border)",
            display: "flex",
            gap: 3,
            justifyContent: "flex-end",
            alignItems: "center",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <Box sx={{ fontSize: 13 }}>
            Debits <strong>{money(totals.debit)}</strong>
          </Box>
          <Box sx={{ fontSize: 13 }}>
            Credits <strong>{money(totals.credit)}</strong>
          </Box>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: totals.balanced ? "var(--color-graphite)" : "#b3261e",
            }}
          >
            {totals.balanced
              ? "Balanced"
              : `Out by ${money(Math.abs(totals.debit - totals.credit))}`}
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
        <Button variant="outlined" onClick={onDone} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canPost || create.isPending}
          onClick={submit}
          sx={{
            textTransform: "none",
            bgcolor: "var(--color-rodeo-black)",
            "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
          }}
        >
          {create.isPending ? "Posting…" : "Post"}
        </Button>
      </Box>
    </Box>
  );
}
